import { Node, Transforms, Element, Editor as SlateEditor } from 'slate';
import { v4 as uuidv4 } from 'uuid';
import { evolveUuids } from 'shared/lib/helper-functions';
import { defaultParagraph } from 'slate/extensions/paragraph';
import { CustomEditor, PageElement } from 'slate/types/slate';

export const isPage = (node: Node): node is PageElement => Element.isElement(node) && node.type === 'page';

export const emptyPage = (revisionId?: string): PageElement => ({
  type: 'page',
  uuid: uuidv4(),
  revisionId,
  children: [
    {
      type: 'layout',
      children: [defaultParagraph(revisionId)],
    },
  ],
});

export const insertPage = (editor: CustomEditor) => {
  if (!editor.selection) {
    return;
  }
  const pageEntry = SlateEditor.above(editor, {
    at: editor.selection.anchor,
    match: (node) => isPage(node),
  });
  if (!pageEntry) {
    return;
  }
  const [currentPage, path] = pageEntry;

  const lastLeaf = Node.texts(currentPage, { reverse: true }).next().value;
  if (!lastLeaf) {
    return;
  }
  const point = { path: [...path, ...lastLeaf[1]], offset: lastLeaf[0].text.length };
  Transforms.insertNodes(editor, emptyPage(), {
    at: point,
    match: (node) => isPage(node),
  });

  Transforms.setSelection(editor, { anchor: { path: [1, 0, 0], offset: 0 }, focus: { path: [1, 0, 0], offset: 0 } });
};

export const prependPage = (editor: CustomEditor) => {
  Transforms.insertNodes(editor, emptyPage(), {
    at: [0],
    match: (node) => isPage(node),
  });

  Transforms.setSelection(editor, { anchor: { path: [1, 0, 0], offset: 0 }, focus: { path: [1, 0, 0], offset: 0 } });
};

export const deletePageAt = (editor: CustomEditor, pageIndex: number) => {
  const targetPath = [pageIndex];
  const fallbackPoint = { anchor: { path: targetPath, offset: 0 }, focus: { path: targetPath, offset: 0 } };
  Transforms.select(editor, fallbackPoint);
  Transforms.removeNodes(editor, {
    at: targetPath,
    match: (node) => isPage(node),
  });
};

export const insertTemplate = (editor: CustomEditor, template: { structure: unknown }, index: number) => {
  const content = evolveUuids(template.structure);
  Transforms.insertNodes(editor, content, {
    at: [index],
    match: (node) => isPage(node),
    select: true,
  });

  editor.onInsertTemplate?.(template);
  // After page insert, popper does not recalculate position of "Add new page" buttons. scrollBy() fixes that
  window.scrollBy(0, 1);
};
